import cn from 'classnames';
import { type ReactNode, useRef, useState } from 'react';

import useClickOutside from '../../hooks/useClickOutside';
import { useEscapeToClose } from '../../hooks/useEscapeToClose';
import styles from './Dropdown.module.css';
import { TriggerButton } from './TriggerButton';

export interface DropdownProps {
    trigger: {
        text: string;
        label?: string;
        hideLabelOnSmallScreens?: boolean;
    };
    slim?: boolean;
    prefix?: ReactNode;
    className?: string;
    children?: ReactNode;
}

export const Dropdown = ({ trigger, slim = false, prefix, className, children }: DropdownProps) => {
    const [open, setOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement | null>(null);

    useClickOutside(dropdownRef, () => setOpen(false));
    useEscapeToClose(open, () => setOpen(false));

    return (
        <div className={cn('w-full flex flex-col relative', className)} ref={dropdownRef}>
            <TriggerButton
                triggerLabel={trigger.label && trigger.label}
                text={trigger.text}
                onTrigger={() => setOpen((prev) => !prev)}
                prefix={prefix}
                slim={slim}
                open={open}
                aria={{ hasPopup: 'listbox', label: trigger.label }}
                hideLabelOnSmallScreen={trigger.hideLabelOnSmallScreens}
            />
            {open && (
                <div
                    className={cn(styles.dropdown, {
                        [styles['dropdown--slim']]: slim,
                        [styles['dropdown--open']]: open,
                        [styles['dropdown--no-label']]: !trigger.label,
                        [styles['dropdown--slim-with-label']]: slim && trigger.label,
                    })}>
                    {children}
                </div>
            )}
        </div>
    );
};
